/****************************************************************************
 * COPYRIGHT (c) 2024 Harman Connected Services
 *
 * All rights reserved
 *
 * This software embodies materials and concepts which are
 * confidential to Ignite Product and is made available solely
 * pursuant to the terms of a written license agreement with
 * Ignite Product.
 *
 * Designed and Developed by Harman International Industries, Inc.
 ***************************************************************************/
import React, { useCallback, useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";

import "./App.css";
import { Provider } from "react-redux";
import store from "./Services/redux/store";
import ApplicationDetailsNew from "./page/Application-Details/ApplicationDetailsNew";
import UserProfile from "./page/user-profile/userProfile";
import UserProfileDeveloper from "./page/user-profile/UserProfileDeveloper";
import ApplicationList from "./page/ApplicationList/applicationList";
import AllRelease from "./page/All Releases/AllRelease";
import { jwtDecode } from "jwt-decode";
import ApplicationTeamsInfo from "./Components/ApplicationTeamsInfo/ApplicationTeamsInfo";
import ActivityLog from "./page/ActivityLog/ActivityLog";
import Dashboard from "./Components/ApplicationStatistics/Developer/Dashboard";
import OEMDashboard from "./page/oem-admin/oem-dashboard";
import Overview from "./page/oem-admin/overview/overview";
import GlobalAdminOverview from "./page/global-admin/global-admin-overview/global-admin-overview";
import CrashReport from "./Components/ApplicationStatistics/Developer/CrashReport";
import OemStatus from "./Components/ApplicationStatistics/Developer/OemStatus";
import Help from "./page/developer/help/help";
import InAppProducts from "./page/In-App-Products/InAppProducts";
import AppProduct from "./page/In-App-Products/app-product/appProduct";
import EditRecurringProduct from "./page/In-App-Products/edit-recurring-product/EditRecurringProduct";
import EditOneTimeProduct from "./page/In-App-Products/edit-one-time-product/EditOneTimeProduct";
import Plan from "./page/In-App-Products/plan/Plan";
import PremiumProducts from "./page/Premium-Products/PremiumProducts/PremiumProducts";
import OEMApplicationList from "./page/oem-admin/application/oem-applicationList";
import OemApplicationDetailsNew from "./page/oem-admin/application-details/oemAdminApplicationDetailsNew";
import Analytics from "./page/developer/analytics";
import ApplicationSummary from "./page/global-admin/Global Admin Reports/application-summary";
import SavePlan from "./page/Premium-Products/save-plan/SavePlan";
import PortfolioSettings from "./page/oem-admin/settings/portfolios/portfolios";
import Categories from "./page/oem-admin/settings/categories/categories";
import FeatureList from "./page/FeatureList/featureList";
import Notifications from "./page/oem-admin/settings/notifications/notifications";
import RecommendedApps from "./page/oem-admin/settings/recommended-apps/RecommendedApps";
import MakeModelYear from "./page/oem-admin/settings/make-model-year/MakeModelYear";
import VehicleListSettings from "./page/oem-admin/settings/portfolios/VehicleListSettings";
import APIManager from "../src/Services/ApiManager/ApiManager";
import PublishInfo from "./page/oem-admin/application/info/PublishInfo";
import OemActivityLog from "./page/oem-admin/application/oem-activityLog/OemActivityLog";
import PortfolioDomainSettings from "./page/domain-approver/portfolio-settings/PortfolioDomainSettings";
import ManageApiClient from "./page/Manage-APi-Client/ManageApiClient";
import DomainApproverApplicationList from "./page/domain-approver/application/domain-approver-applicationList";
import DomainApproverApplicationDetails from "./page/domain-approver/application-details/DomainApproverApplicationDetails";
import GlobalAdminApplicationDetails from "./page/global-admin/Application Details/global-admin-application-details";
import DSAComplaintUrl from "./page/oem-admin/settings/DSAComplaintUrl/DSAComplaintUrl";
import QaProcess from "./Components/QaProcess/QaProcess";
import WelcomeWizard from "./Components/WelcomeWizard/WelcomeWizard";
import FlexMetaData from "./page/oem-admin/settings/flexmetadata/FlexMetaData";

// JWT Components
import JWTExpiryDialog from "./Components/JWTExpiryDialog/JWTExpiryDialog";
import useJWTTokenManager from "./hooks/useJWTTokenManager";
import { getConfig } from "./config";
import ErrorPage from "./page/Error/ErrorPage";
import GlobalAdminDashboard from "./page/global-admin/globalAdminDashboard";
import GlobalAdminApplicationList from "./page/global-admin/application/global-admin-applicationList";
import Users from "./page/global-admin/users/Users";
import GlobalAdminCategories from "./page/global-admin/categories/GlobalAdminCategories";
import AddCategories from "./page/global-admin/categories/AddCategories";
import Documentation from "./page/developer/Documentation/Documentation";
import Codelab from "./page/developer/Codelab/Codelab";
import Tools from "./page/developer/Tools/Tools";
import MFAReconfigure from "./Components/MFALogin/MFAReconfigure";
import QRCodePage from "./Components/MFALogin/QRCodePage";
import { getAllAppsBasedOnStatus, getAllApplications } from "./utils/appUtils";
import SecretKeyPage from "./Components/MFALogin/SecretKeyPage";
import GlobalAdminFlexMetadata from "./page/global-admin/settings/flex-metadata/GlobalAdminFlexMetadata";
import AgreementNotification from "./Components/AgreementNotification/AgreementNotification";
import DSAReviewPopup from "./Components/DSAReviewPopup/DSAReviewPopup";
import DSAMissingInfoPopup from "./Components/DSAMissingInfoPopup/DSAMissingInfoPopup";
import { checkDsaReviewStatus } from "./Services/redux/dsa-review/DSAReviewActions";

const safeDecodeToken = (token) => {
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
};

// Standalone token validation function to avoid circular dependencies
const isTokenValid = () => {
  const accessToken = sessionStorage.getItem("access_token");
  if (!accessToken) {
    return false;
  }

  const decodedToken = safeDecodeToken(accessToken);
  if (!decodedToken?.exp) {
    sessionStorage.removeItem("access_token");
    return false;
  }

  const currentTime = Math.floor(Date.now() / 1000);
  return decodedToken.exp > currentTime + 300;
};

const PORTAL_SCOPE_MAP = {
  "oem-admin":
    "rs%2FigniteStorePortfolioManager%20openid%20aws.cognito.signin.user.admin",
  "domain-approver":
    "rs%2FigniteStoreDomainApprover%20openid%20aws.cognito.signin.user.admin",
  "global-admin":
    "rs%2FigniteStoreAdmin%20openid%20aws.cognito.signin.user.admin",
  seller: "rs%2FigniteStoreSeller%20openid%20aws.cognito.signin.user.admin",
};

const getPortalScope = (portal) => PORTAL_SCOPE_MAP[portal] || PORTAL_SCOPE_MAP.seller;

const hasNewAgreementAvailable = (agreementDetails) =>
  agreementDetails?.isNewAgreementAvailable === true ||
  agreementDetails?.status === "NEW_UPDATE_AVAILABLE";

const getPortalClientId = (config, portal) => {
  const clientMap = {
    "oem-admin": config.REACT_APP_OEM_ADMIN_CLIENT_ID,
    seller: config.REACT_APP_CLIENT_ID,
    "domain-approver": config.REACT_APP_DOMAIN_APPROVER_CLIENT_ID,
    "global-admin": config.REACT_APP_GLOBAL_ADMIN_CLIENT_ID,
  };
  return clientMap[portal] || "";
};

const getPortalVwClientId = (config, portal) => {
  const clientMap = {
    "oem-admin": config.REACT_APP_VW_OEM_ADMIN_CLIENT_ID,
    seller: config.REACT_APP_VW_CLIENT_ID,
    "domain-approver": config.REACT_APP_VW_DOMAIN_APPROVER_CLIENT_ID,
  };
  return clientMap[portal] || "";
};

const buildCognitoLoginUrl = ({
  cognitoDomain,
  clientId,
  redirectUri,
  scope,
  portal,
}) => {
  if (!clientId) {
    return "";
  }

  return `${cognitoDomain}/login?client_id=${clientId}&response_type=code&scope=${scope}&redirect_uri=${redirectUri}&portal=${portal}`;
};

const createRefSetter = (ref) => (value) => {
  ref.current = typeof value === "function" ? value(ref.current) : value;
};

export default function App() {
  const [tokens, setTokens] = useState([]);
  const [isTokenExchanged, setIsTokenExchanged] = useState(false);
  const componentNameRef = useRef("");
  const openErrorPopupRef = useRef(false);
  const errorMessageRef = useRef("");
  const headerMessageRef = useRef("");
  const loaderRef = useRef(false);
  const [invalidUserLogin, setInvalidUserLogin] = useState(false);
  const [invalidUserLoginMessage, setInvalidUserLoginMessage] = useState("");
  const [pendingApprovalUser, setPendingApprovalUser] = useState(false);
  const applicationListRef = useRef({});
  const completeApplicationListRef = useRef({});
  const [showNoAppImage, setShowNoAppImage] = useState(false);
  const [showAgreementNotification, setShowAgreementNotification] =
    useState(false);

  const setComponentName = useCallback(createRefSetter(componentNameRef), []);
  const setOpenErrorPopup = useCallback(createRefSetter(openErrorPopupRef), []);
  const setErrorMessage = useCallback(createRefSetter(errorMessageRef), []);
  const setHeaderMessage = useCallback(createRefSetter(headerMessageRef), []);
  const setLoader = useCallback(createRefSetter(loaderRef), []);
  const setApplicationList = useCallback(createRefSetter(applicationListRef), []);
  const setCompleteApplicationList = useCallback(
    createRefSetter(completeApplicationListRef),
    [],
  );

  const params = new URLSearchParams(globalThis.location.search);
  const authorization_code = params.get("code");

  const navigate = useNavigate();
  const config = getConfig();

  const portals = {
    igniteStoreSeller: "seller",
    igniteStorePortfolioManager: "oem-admin",
    igniteStorePortfolioManager_ReadOnly: "oem-admin",
    igniteStoreDomainApprover: "domain-approver",
    igniteStoreDomainApprover_ReadOnly: "domain-approver",
    igniteStoreAdmin: "global-admin",
    igniteStoreAdmin_ReadOnly: "global-admin",
  };

  const cognitoDomain = config.REACT_APP_COGNITO_DOMAIN;
  const redirect_uri = config.REACT_APP_REDIRECT_URI;
  const app_portal = config.REACT_APP_PORTAL;

  const clientId = getPortalClientId(config, app_portal);
  const vw_client_id = getPortalVwClientId(config, app_portal);
  const scope = getPortalScope(app_portal);

  const domainApproverScope = getPortalScope("domain-approver");
  const globalAdminScope = getPortalScope("global-admin");
  const loginUrl = buildCognitoLoginUrl({
    cognitoDomain,
    clientId,
    redirectUri: redirect_uri,
    scope,
    portal: app_portal,
  });

  //  VW login
  const vwLoginUrl = globalThis.location.href.includes("/vw")
    ? buildCognitoLoginUrl({
        cognitoDomain,
        clientId: vw_client_id,
        redirectUri: redirect_uri,
        scope,
        portal: app_portal,
      })
    : "";
  // login URL for domain approver
  const domainApproverLoginUrl = buildCognitoLoginUrl({
    cognitoDomain,
    clientId: config.REACT_APP_DOMAIN_APPROVER_CLIENT_ID,
    redirectUri: redirect_uri,
    scope: domainApproverScope,
    portal: app_portal,
  });

  // login URL for Global Admin
  const globalAdminLoginUrl = buildCognitoLoginUrl({
    cognitoDomain,
    clientId: config.REACT_APP_GLOBAL_ADMIN_CLIENT_ID,
    redirectUri: redirect_uri,
    scope: globalAdminScope,
    portal: app_portal,
  });

  const decodeTokenData = useCallback(() => {
    const decoded = jwtDecode(sessionStorage.getItem("id_token"));
    const custom_domain_portal = checkCustomDomainAndCustomPortal(decoded);

    if (app_portal === "global-admin") {
      globalAdminRoleCheck(decoded);
    } else {
      checkCustomRole(decoded);
    }

    const domain = custom_domain_portal.domain;
    sessionStorage.setItem("domain", domain);
  }, [app_portal]);

  const storeSessionTokens = (newTokens) => {
    setTokens(newTokens);
    sessionStorage.setItem("access_token", newTokens.access_token);
    sessionStorage.setItem("id_token", newTokens.id_token);
    sessionStorage.setItem("refresh_token", newTokens.refresh_token);
    sessionStorage.setItem("expires_in", newTokens.expires_in);
  };

  const persistDecodedTokenMetadata = (decodedIdToken, decodedAccessToken) => {
    const newScope = (decodedAccessToken.scope || "").split(" ");
    sessionStorage.setItem("scopes", JSON.stringify(newScope));
    sessionStorage.setItem(
      "companyName",
      decodedIdToken["custom:companyName"] || "",
    );
    sessionStorage.setItem("payment_role", decodedIdToken["custom:scp"] || "NA");
    sessionStorage.setItem("customPortal", decodedIdToken["custom:portal"]);
    sessionStorage.setItem("domain", decodedIdToken["custom:domain"]);
  };

  const persistSellerAttributes = (decodedIdToken) => {
    if (sessionStorage.getItem("idpLogin") !== "true") {
      sessionStorage.setItem("portfolioId", decodedIdToken["custom:domain"]);
      if (app_portal === "seller") {
        const oemSeller = isOemSeller();
        if (oemSeller) {
          sessionStorage.setItem("isOemSeller", oemSeller);
        }
      }
    }
  };

  const processPortalAfterTokenPersist = (decodedIdToken) => {
    if (app_portal === "oem-admin" || app_portal === "global-admin") {
      if (decodedIdToken["identities"]) {
        sessionStorage.setItem("IDP_TOKEN", decodedIdToken["custom:externalId"]);
      }

      if (app_portal === "oem-admin") {
        callOEMAdminSettingsApi();
      } else {
        callGlobalAdminPortalConfigApi();
      }
      return;
    }

    decodeTokenData();
  };

  const persistTokenData = useCallback(
    (newTokens) => {
      storeSessionTokens(newTokens);

      const decodedIdToken = safeDecodeToken(newTokens.id_token);
      const decodedAccessToken = safeDecodeToken(newTokens.access_token);
      if (!decodedIdToken || !decodedAccessToken) {
        return;
      }

      persistDecodedTokenMetadata(decodedIdToken, decodedAccessToken);

      checkAppScopeAndRoles(newTokens.id_token);
      persistSellerAttributes(decodedIdToken);
      processPortalAfterTokenPersist(decodedIdToken);
    },
    [app_portal, decodeTokenData],
  );

  const exchangeCodeForTokens = useCallback(
    async (authorization_code) => {
      if (!sessionStorage.getItem("idpLogin")) {
        sessionStorage.setItem("idpLogin", false);
      }
      const tokenUrl = `${cognitoDomain}/oauth2/token`;
      const body = new URLSearchParams({
        grant_type: "authorization_code",
        client_id:
          sessionStorage.getItem("idpLogin") &&
          sessionStorage.getItem("idpLogin") !== "false"
            ? vw_client_id
            : clientId,
        code: authorization_code,
        redirect_uri: redirect_uri,
      });

      try {
        const response = await fetch(tokenUrl, {
          method: "POST",
          headers: { "Content-Type": "application/x-www-form-urlencoded" },
          body: body.toString(),
        });
        const fetchedTokens = await response.json();
        if (fetchedTokens.access_token) {
          persistTokenData(fetchedTokens);
        }
      } catch (error) {
        setErrorMessage(error?.message || "Failed to exchange authorization code");
        setHeaderMessage("Error");
        setOpenErrorPopup(true);
        setLoader(false);
      }
    },
    [
      cognitoDomain,
      vw_client_id,
      clientId,
      redirect_uri,
      app_portal,
      persistTokenData,
    ],
  );

  const isSeller = app_portal === "seller";
  const isGlobalAdmin = app_portal === "global-admin";
  const isDomainApproverPortal = app_portal === "domain-approver";

  const loadSellerApplications = useCallback(() => {
    const handlers = {
      setLoader,
      setApplicationList,
      setErrorMessage,
      setOpenErrorPopup,
      setShowNoAppImage,
      setComponentName,
      setHeaderMessage,
      setCompleteApplicationList,
    };

    const selectedStatus = sessionStorage.getItem("selectedAppStatus");
    if (selectedStatus && selectedStatus !== "all") {
      getAllAppsBasedOnStatus(selectedStatus, handlers);
      return;
    }

    getAllApplications(handlers);
  }, []);

  const handleCognitoSelfSuccess = useCallback(
    (json) => {
      const firstResult = json?.results?.[0];

      if (
        firstResult?.customMFAEnabled === true &&
        firstResult?.userMFASettingList?.length === 0
      ) {
        setLoader(false);
        const mfaFlowPaths = ['/ui/mfa-reconfigure', '/ui/qr-code-page', '/ui/secret-key-page'];
        if (!mfaFlowPaths.includes(globalThis.location.pathname)) {
          navigate("/ui/mfa-reconfigure");
        }
        return;
      }

      if (
        json?.[0] !== undefined &&
        json?.[0]?.detailedErrorCode === "USER_AUTHENTICATION_EXCEPTION"
      ) {
        setErrorMessage(json[0].message);
        setOpenErrorPopup(true);
        setHeaderMessage("401 Unauthorized");
        setComponentName("userAuthFail");
        setLoader(false);
        return;
      }

      if (!(json?.results !== undefined && json?.messages?.[0]?.key === "success.key")) {
        setLoader(false);
        return;
      }

      sessionStorage.setItem("userId", firstResult?.id || "");
      sessionStorage.setItem(
        "setCorporateContactName",
        firstResult?.corporateContactName || "",
      );

      if (!isSeller) {
        setLoader(false);
        return;
      }

      if (hasNewAgreementAvailable(firstResult?.agreementDetails)) {
        setShowAgreementNotification(true);
      }

      if (firstResult?.dsaReviewed === false || firstResult?.dsaReviewed === true) {
        store.dispatch(checkDsaReviewStatus(firstResult));
      }

      if (
        firstResult?.igniteStoreAgreementStatus === undefined &&
        sessionStorage.getItem("idpLogin") !== "true"
      ) {
        sessionStorage.setItem("agreementPending", "true");
        setLoader(false);
        if (!globalThis.location.pathname.includes("/documentation")) {
          navigate("/ui/developer/myprofile");
        }
        return;
      }

      sessionStorage.removeItem("agreementPending");
      loadSellerApplications();
    },
    [isSeller, loadSellerApplications, navigate],
  );

  const handleCognitoSelfError = useCallback((error) => {
    if (error.message === "Scope Doesn't match With User Attribute Scope") {
      setInvalidUserLogin(true);
      setInvalidUserLoginMessage(
        `${sessionStorage.getItem("customPortal")} user is trying to login in seller portal`,
      );
      setLoader(false);
      sessionStorage.setItem("invalidUser", true);
    } else if (
      error.message ===
      "Your account creation is pending for approval. Please try after sometime"
    ) {
      setPendingApprovalUser(true);
      sessionStorage.setItem("pendingApproval", true);
    } else {
      setErrorMessage(error.message);
    }

    if (error?.message?.toLowerCase().includes("jwt expired")) {
      setComponentName("appError");
      setHeaderMessage("401 Token Expired");
    } else {
      setHeaderMessage("Error");
    }

    if (error.message !== "Scope Doesn't match With User Attribute Scope") {
      setOpenErrorPopup(true);
      setLoader(false);
    }
  }, []);

  useEffect(() => {
  if (!tokens.access_token) {
    return;
  }
    setLoader(true);
    if (isSeller) {
      APIManager.getCognitoSelf()
        .then(handleCognitoSelfSuccess)
        .catch(handleCognitoSelfError);
    } else if (isDomainApproverPortal || isGlobalAdmin) {
      APIManager.getCognitoAdminSelf()
        .then(handleCognitoSelfSuccess)
        .catch(handleCognitoSelfError);
    }
  }, [
    tokens.access_token,
    isSeller,
    isDomainApproverPortal,
    isGlobalAdmin,
    handleCognitoSelfSuccess,
    handleCognitoSelfError,
  ]);

  const checkAppScopeAndRoles = (idToken) => {
    const decoded = jwtDecode(idToken);

    const portalScope = decoded["custom:portal"];

    if (app_portal !== portalScope) {
      navigate("/ui/error", {
        state: {
          errorMessage: `${
            portals[decoded["custom:scope"]]
          } user is trying to login ${app_portal} portal.`,
        },
      });
    }
  };

  const checkRolesType = (roles, scope, property, readOnlyValue) => {
    const scopeConfig = config[scope] || [];
    if (Array.isArray(roles)) {
      roles.some((role) => scopeConfig.includes(role))
        ? sessionStorage.setItem(property, readOnlyValue)
        : sessionStorage.setItem(property, !readOnlyValue);
    } else {
      scopeConfig.includes(roles)
        ? sessionStorage.setItem(property, readOnlyValue)
        : sessionStorage.setItem(property, !readOnlyValue);
    }
  };

  const checkScopeAndRoles = (role, decoded, idpRoles_2) => {
    let scope = decoded["custom:scope"];
    let roles = Array.isArray(JSON.parse(role)) ? JSON.parse(role) : role;
    let userScopeValue;

    sessionStorage.setItem("roles", JSON.stringify([idpRoles_2]));
    sessionStorage.setItem("allRoles", JSON.stringify([idpRoles_2]));

    if (decoded["custom:displayName"])
      sessionStorage.setItem("displayName", decoded["custom:displayName"]);

    switch (scope) {
      case "igniteStoreSeller":
        if (sessionStorage.getItem("idpLogin") !== "true") {
          const oemSeller = isOemSeller();
          if (oemSeller) {
            sessionStorage.setItem("isOemSeller", oemSeller);
          }
        }
        break;

      case "igniteStorePortfolioManager":
        userScopeValue = isOemAdmin();
        checkRolesType(roles, scope, "readonly_OemAdmin", false);
        break;

      case "igniteStorePortfolioManager_ReadOnly":
        userScopeValue = isOemAdmin();
        checkRolesType(roles, scope, "readonly_OemAdmin", true);
        break;

      case "igniteStoreDomainApprover":
        checkRolesType(roles, scope, "readonly_DomainApprover", false);
        break;

      case "igniteStoreDomainApprover_ReadOnly":
        checkRolesType(roles, scope, "readonly_DomainApprover", true);
        break;

      case "igniteStoreAdmin":
        userScopeValue = isAdmin();
        break;
      case "igniteStoreAdmin_ReadOnly":
        userScopeValue = isAdmin();
        break;

      default:
        break;
    }
    return userScopeValue;
  };
  const isAdmin = () => {
    if (sessionStorage.getItem("scopes") != null) {
      let portalConfigData = JSON.parse(sessionStorage.getItem("portalConfig"));
      return (
        sessionStorage
          .getItem("scopes")
          .includes(portalConfigData[0].config.scope)
      );
    }
  };

  const checkCustomDomainAndCustomPortal = (decoded) => {
    let domain, custom_portal, custom_scope;
    for (const key of Object.keys(decoded)) {
      if (key === "custom:domain") {
        domain = decoded[key];
      }
      if (key === "custom:portal") {
        custom_portal = decoded[key];
      }
      if (key === "custom:scope") {
        custom_scope = decoded[key];
      }
    }
    return { domain, custom_portal, custom_scope };
  };

  const checkCustomRole = (decoded) => {
    let role;
    let idpRoles_2 = [];
    if (decoded["identities"]) {
      sessionStorage.setItem("IDP_TOKEN", decoded["custom:externalId"]);
    }
    for (const key_2 of Object.keys(decoded)) {
      if (key_2 === "custom:role") {
        role = decoded[key_2];
        idpRoles_2 = Array.isArray(JSON.parse(role)) ? JSON.parse(role) : role;
        const userScopeValue = checkScopeAndRoles(role, decoded, idpRoles_2);

        if (userScopeValue === false) {
          navigate("/ui/error", {
            state: {
              errorMessage: `${sessionStorage.getItem(
                "customPortal",
              )} is trying to login in ${app_portal} portal`,
            },
          });
          break;
        }
      }
    }
  };

  const globalAdminRoleCheck = useCallback((decoded) => {
    if (
      decoded["custom:role"] === "IGNITESTOREGLOBALADMIN_ROLE_READONLY" &&
      decoded["custom:scope"] === "igniteStoreAdmin_ReadOnly"
    ) {
      sessionStorage.setItem("readonly_GlobalAdmin", true);
    } else {
      sessionStorage.setItem("readonly_GlobalAdmin", false);
    }
  }, []);

  const isOemSeller = () => {
    if (
      sessionStorage.getItem("scopes") != null &&
      sessionStorage.getItem("roles") === null
    ) {
      return sessionStorage.getItem("domain") !== "NONE";
    }
  };

  const isOemAdmin = () => {
    if (sessionStorage.getItem("roles") !== null) {
      let portalConfigData = JSON.parse(sessionStorage.getItem("portalConfig"));
      if (sessionStorage.getItem("idpLogin") === "true") {
        let roles = JSON.parse(sessionStorage.getItem("roles"));
        if (app_portal === "oem-admin") {
          let adminRoles = portalConfigData?.settings?.oemAdminRoles;
          let userScoope = adminRoles.some((r) => roles[0].includes(r));
          return userScoope;
        } else {
          return false;
        }
      } else {
        let roles = sessionStorage.getItem("roles");
        if (app_portal === "oem-admin") {
          let adminRles = portalConfigData.settings?.oemAdminCommonRoles;
          console.log("adminrles=====>", adminRles);
          return adminRles.some((r) => roles.includes(r));
        } else {
          return false;
        }
      }
    }
  };

  const handleLogout = useCallback(() => {
    const isIdpLogin = sessionStorage.getItem("idpLogin") === "true";
    const scopeId = getPortalScope(app_portal);
    const logoutClientId = isIdpLogin
      ? getPortalVwClientId(config, app_portal) || getPortalClientId(config, app_portal)
      : getPortalClientId(config, app_portal);

    // Check if this is IDP (VW) login or regular login
    if (
      sessionStorage.getItem("idpLogin") &&
      sessionStorage.getItem("idpLogin") === "false"
    ) {
      // Regular Cognito logout
      const logoutUrl = `${cognitoDomain}/logout?client_id=${logoutClientId}&response_type=code&scope=${scopeId}&redirect_uri=${redirect_uri}&portal=${app_portal}`;
      sessionStorage.clear();
      globalThis.location.href = logoutUrl;
    } else {
      // IDP (VW) logout
      const logoutUrl = `${
        config.REACT_APP_VW_LOGOUT_URL
      }${sessionStorage.getItem(
        "IDP_TOKEN",
      )}&post_logout_redirect_uri=${redirect_uri}&state=logout_state_1`;
      sessionStorage.clear();
      sessionStorage.setItem("idpLogout", true);
      globalThis.location.href = logoutUrl;
    }
  }, [cognitoDomain, redirect_uri, app_portal, config]);

  // Initialize JWT Token Manager
  const jwtManager = useJWTTokenManager(tokens, setTokens, handleLogout);

  const redirectToCognitoLogin = useCallback(() => {
    if (sessionStorage.getItem("idpLogout") === "true") {
      const scopeId = getPortalScope(app_portal);
      const portal = app_portal;
      const newVWLoginURl = ` ${cognitoDomain}/login?client_id=${vw_client_id}&redirect_uri=${redirect_uri}&response_type=code&scope=${scopeId}&portal=${portal}`;
      globalThis.location.href = newVWLoginURl;
      sessionStorage.setItem("idpLogin", true);
      return;
    }

    if (globalThis.location.href.includes("/vw")) {
      globalThis.location.href = vwLoginUrl;
      sessionStorage.setItem("idpLogin", true);
      return;
    }

    if (app_portal === "domain-approver") {
      globalThis.location.href = domainApproverLoginUrl;
      return;
    }

    if (app_portal === "global-admin" || app_portal === "harman-admin") {
      globalThis.location.href = globalAdminLoginUrl;
      return;
    }

    sessionStorage.setItem("idpLogin", false);
    globalThis.location.href = loginUrl;
  }, [
    loginUrl,
    vwLoginUrl,
    cognitoDomain,
    redirect_uri,
    vw_client_id,
    app_portal,
    domainApproverLoginUrl,
    globalAdminLoginUrl,
  ]);

  useEffect(() => {
    // If we have an authorization code, exchange it for tokens

    if (authorization_code && !isTokenExchanged) {
      setIsTokenExchanged(true);
      exchangeCodeForTokens(authorization_code);
      return;
    }

    // Check if we already have valid tokens
    if (isTokenValid()) {
      const token = {
        access_token: sessionStorage.getItem("access_token"),
        expires_in: sessionStorage.getItem("expires_in"),
        id_token: sessionStorage.getItem("id_token"),
        refresh_token: sessionStorage.getItem("refresh_token"),
        token_type: "Bearer",
      };
      if (app_portal === "oem-admin") {
        callOEMAdminSettingsApi();
      } else if (app_portal === "global-admin") {
        callGlobalAdminPortalConfigApi();
      }
      setTokens(token);
      return;
    }

    // Only redirect to login if we don't have an authorization code and no valid tokens
    if (
      !authorization_code &&
      !tokens.access_token &&
      !sessionStorage.getItem("access_token")
    ) {
      redirectToCognitoLogin();
    }
  }, [
    authorization_code,
    isTokenExchanged,
    tokens.access_token,
    redirectToCognitoLogin,
    exchangeCodeForTokens,
    app_portal,
  ]);

  const callOEMAdminSettingsApi = useCallback(() => {
    APIManager.getPortalConfigSettings()
      .then((response) => {
        sessionStorage.setItem("portalConfig", JSON.stringify(response));
        decodeTokenData();
      })
      .catch((error) => {
        sessionStorage.setItem("invalidUserLogin", true);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
        navigate("/ui/error", {
          state: {
            errorMessage: `${sessionStorage.getItem(
              "customPortal",
            )} is trying to login in ${app_portal} portal`,
          },
        });
        setOpenErrorPopup(true);
      });
  }, [app_portal, decodeTokenData, navigate]);

  const callGlobalAdminPortalConfigApi = useCallback(() => {
    APIManager.getPortalConfigGlobalAdmin()
      .then((response) => {
        sessionStorage.setItem("portalConfig", JSON.stringify(response));
        decodeTokenData();
      })
      .catch((error) => {
        sessionStorage.setItem("invalidUserLogin", true);
        setComponentName("appError");
        setErrorMessage(error.message);
        setHeaderMessage(
          error.message === "jwt expired" ? "401 Token Expired" : "Error",
        );
      });
  }, [decodeTokenData]);
  return (
    <Provider store={store}>
      {/* Agreement Notification */}
      {showAgreementNotification && isSeller && (
        <AgreementNotification
          onClose={() => {
            setShowAgreementNotification(false);
          }}
          onClick={() => {
            setShowAgreementNotification(false);
            navigate("/ui/developer/myprofile");
          }}
        />
      )}

      {isSeller && <DSAReviewPopup />}
      {isSeller && <DSAMissingInfoPopup />}

      {tokens?.access_token && (
        <div>
          <Routes>
            <Route
              path="/ui/developer/applications"
              element={
                <ApplicationList
                  showNoAppImage={showNoAppImage}
                  setShowNoAppImage={setShowNoAppImage}
                  invalidUserLogin={invalidUserLogin}
                  setInvalidUserLogin={setInvalidUserLogin}
                  invalidUserLoginMessage={invalidUserLoginMessage}
                  setInvalidUserLoginMessage={setInvalidUserLoginMessage}
                  pendingApprovalUser={pendingApprovalUser}
                  setPendingApprovalUser={setPendingApprovalUser}
                />
              }
            />
            <Route path="/ui/developer/features" element={<FeatureList />} />
            <Route
              path="/ui/domain-approver/applications"
              element={<DomainApproverApplicationList />}
            />
            <Route
              path="/ui/domain-approver/portfolio-settings"
              element={<PortfolioDomainSettings />}
            />
            <Route
              path="/ui/domain-approver/categories/add-new-category"
              element={<AddCategories isDomainApprover={true} />}
            />
            <Route
              path="/ui/domain-approver/categories/:categoryId"
              element={<AddCategories isDomainApprover={true} />}
            />
            <Route
              path="/ui/harman-admin/dashboard"
              element={<GlobalAdminDashboard />}
            />
            <Route
              path="/ui/harman-admin/applications"
              element={<GlobalAdminApplicationList />}
            />
            <Route
              path="/ui/harman-admin/application/:appId/submission/:submissionId"
              element={<GlobalAdminApplicationDetails />}
            />
            <Route
              path="/ui/harman-admin/application/:appId/submission/:submissionId/publisher-info"
              element={<PublishInfo />}
            ></Route>
            <Route
              path="/ui/harman-admin/application/:appId/submission/:submissionId/releases"
              element={<AllRelease />}
            />
            <Route path="/ui/harman-admin/users" element={<Users />} />
            <Route
              path="/ui/harman-admin/flex-metadata"
              element={<GlobalAdminFlexMetadata />}
            />
            <Route
              path="/ui/harman-admin/categories"
              element={<GlobalAdminCategories />}
            />
            <Route
              path="/ui/harman-admin/application/:appId/submission/:submissionId/activity-log"
              element={<ActivityLog />}
            />
            <Route
              path="/ui/harman-admin/application/:appId/submission/:submissionId/qa-process"
              element={<QaProcess />}
            />
            <Route
              path="/ui/harman-admin/categories/add-new-category"
              element={<AddCategories />}
            />
            <Route
              path="/ui/harman-admin/categories/:categoryId"
              element={<AddCategories />}
            />
            <Route
              path="/ui/harman-admin/tableau-reports"
              element={<Analytics />}
            />
            <Route
              path="/ui/harman-admin/admin-reports"
              element={<ApplicationSummary />}
            />
            <Route
              path="/ui/harman-admin/user-profile"
              element={<UserProfile />}
            />
            <Route
              path="/ui/domain-approver/applications/:appId/submission/:submissionId"
              element={<DomainApproverApplicationDetails />}
            />
            <Route
              path="/ui/domain-approver/applications/:appId/third-party-apps"
              element={<DomainApproverApplicationDetails />}
            />
            <Route path="/ui/oem-admin/dashboard" element={<OEMDashboard />} />

            <Route
              path="/ui/oem-admin/dashboard/application/:contentID"
              element={<Overview />}
            />

            <Route
              path="/ui/global-admin/dashboard/application/:contentID/:packageName"
              element={<GlobalAdminOverview />}
            />

            <Route
              path="/ui/oem-admin/applications"
              element={<OEMApplicationList />}
            />
            <Route
              path="/ui/oem-admin/applications/:portfolioId/content/:appId"
              element={<OemApplicationDetailsNew />}
            ></Route>
            <Route
              path="/ui/oem-admin/publisher-info/:portfolioId/:appId"
              element={<PublishInfo />}
            ></Route>
            <Route
              path="/ui/oem-admin/settings"
              element={<PortfolioSettings />}
            />
            <Route path="/ui/oem-admin/categories" element={<Categories />} />
            <Route
              path="/ui/oem-admin/notifications"
              element={<Notifications />}
            />
            <Route
              path="/ui/oem-admin/flex-metadata"
              element={<FlexMetaData />}
            />
            <Route
              path="/ui/oem-admin/recommended-apps"
              element={<RecommendedApps />}
            />
            <Route
              path="/ui/oem-admin/make-model-year"
              element={<MakeModelYear />}
            />
            <Route
              path="/ui/oem-admin/closed-beta"
              element={<VehicleListSettings />}
            />
            <Route
              path="/ui/oem-admin/applications/:portfolioId/content/:submissionId/activity-log"
              element={<OemActivityLog />}
            />
            <Route
              path="/ui/developer/applications/:appId/"
              element={<ApplicationDetailsNew />}
            >
              
            </Route>
            <Route
              path="/ui/developer/applications/:appId/teamsInfo"
              element={<ApplicationTeamsInfo />}
            />
            <Route
              path="/ui/developer/applications/:appId/submission/:submissionId/dashboard"
              element={<Dashboard />}
            />
            <Route
              path="/ui/developer/applications/:appId/submission/:submissionId/crash-report"
              element={<CrashReport />}
            />
            <Route
              path="/ui/developer/applications/:appId/submission/:submissionId/oem-status"
              element={<OemStatus />}
            />
            <Route
              path="/ui/developer/applications/:appId/submission/:submissionId/activity-log"
              element={<ActivityLog />}
            />
            <Route
              path="/ui/domain-approver/applications/:appId/submission/:submissionId/activity-log"
              element={<ActivityLog />}
            />
            <Route
              path="/ui/developer/applications/:appId/submission/:submissionId/releases"
              element={<AllRelease />}
            />
            <Route path="/ui/error" element={<ErrorPage />} />
            <Route
              path="/ui/oem-admin/settings/dsa-complaint-url"
              element={<DSAComplaintUrl />}
            />
            <Route
              path="/ui/developer/api-client/applications/:appId"
              element={<ManageApiClient />}
            />
            <Route
              path="/ui/domain-approver/applications/:appId/submission/:submissionId/releases"
              element={<AllRelease />}
            />
            <Route
              path="/ui/developer/applications/:appId/app-product"
              element={<InAppProducts />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/add-app-product"
              element={<AppProduct />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/edit-recurring-product/:productId"
              element={<EditRecurringProduct />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/edit-one-time-product/:productId"
              element={<EditOneTimeProduct />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/plan/:productId"
              element={<Plan />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/plan/:productId/edit/:planId"
              element={<Plan />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/premium-product"
              element={<PremiumProducts />}
            ></Route>
            <Route
              path="/ui/developer/analytics"
              element={<Analytics />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/premium-product/plan"
              element={<SavePlan />}
            ></Route>
            <Route
              path="/ui/developer/applications/:appId/premium-product/:productId/plan/:planId"
              element={<SavePlan />}
            ></Route>
            <Route
              path="/ui/developer/documentation"
              element={<Documentation />}
            />
            <Route path="/ui/developer/codelab" element={<Codelab />} />
            <Route path="/ui/developer/tools" element={<Tools />} />
            <Route
              path="/ui/developer/myprofile"
              element={<UserProfileDeveloper />}
            />
            <Route
              path="/ui/oem-admin/user-profile"
              element={<UserProfile />}
            />
            <Route
              path="/ui/domain-approver/user-profile"
              element={<UserProfile />}
            />
            <Route path="/ui/developer/help" element={<Help />} />

            <Route path="/ui/welcome-wizard" element={<WelcomeWizard />} />

            <Route path="*" element={<Navigate to="/" />} />
            <Route path="/ui/mfa-reconfigure" element={<MFAReconfigure />} />
            <Route path="/ui/qr-code-page" element={<QRCodePage />} />
            <Route path="/ui/secret-key-page" element={<SecretKeyPage />} />
          </Routes>
        </div>
      )}

      {/* JWT Expiry Dialog */}
      <JWTExpiryDialog
        open={jwtManager.showExpiryDialog}
        remainingSeconds={jwtManager.remainingSeconds}
        isRefreshingToken={jwtManager.isRefreshingToken}
        onContinue={jwtManager.handleContinueSession}
        onLogout={jwtManager.handleLogoutFromDialog}
      />
    </Provider>
  );
}
